import type { MessageKey, MessageNode, MessageParams, Messages } from './types';

const PLACEHOLDER = /\{(\w+)\}/g;

function lookup(source: MessageNode, key: string): string | undefined {
  let node: string | MessageNode | undefined = source;

  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = node[segment];
  }

  return typeof node === 'string' ? node : undefined;
}

export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;

  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export interface TranslateOptions {
  /** Consulted when the active catalogue has no entry for the key. */
  readonly fallback?: Messages | undefined;
  /** Called once per miss. Wire this to a console warning in development. */
  readonly onMissing?: ((key: string) => void) | undefined;
}

/**
 * Resolve a message key against a catalogue.
 *
 * On a complete miss the key itself is returned. That is deliberate: a visible
 * `nav.primary.portfolio` in the UI is noticed and fixed, whereas an empty
 * string looks like an intentional blank and ships.
 */
export function translate(
  messages: Messages,
  key: MessageKey | (string & {}),
  params?: MessageParams,
  options: TranslateOptions = {},
): string {
  const direct = lookup(messages as MessageNode, key);
  if (direct !== undefined) return interpolate(direct, params);

  options.onMissing?.(key);

  const fallback = options.fallback ? lookup(options.fallback as MessageNode, key) : undefined;
  return fallback === undefined ? key : interpolate(fallback, params);
}
